import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import Layout from './components/layout/Layout';

// Users import
import Users from './pages/users/Users';
import UserDetail from './pages/users/UserDetail'; // Add this import
import Sellers from './pages/users/Sellers';
import SellerDetail from './pages/users/SellerDetail'; // Add this import

// Listings import
import Listings from './pages/listings/Listings';
import ListingDetail from './pages/listings/ListingDetail';
import ListingModeration from './pages/listings/ListingModeration';

// Orders import
import Orders from './pages/orders/Orders';
import OrderDetail from './pages/orders/OrderDetail';
import OrderDisputes from './pages/orders/OrderDisputes';

// Import finance components
import Transactions from './pages/finance/Transactions';
import TransactionDetail from './pages/finance/TransactionDetail';
import Payments from './pages/finance/Payments';
import PaymentDetail from './pages/finance/PaymentDetail';
import Wallets from './pages/finance/Wallets';
import WalletDetail from './pages/finance/WalletDetail';
import FeeSettings from './pages/finance/FeeSettings';
import FeeAnalytics from './pages/finance/FeeAnalytics';
import FeeTransactions from './pages/finance/FeeTransactions';
import FinancialReports from './pages/finance/FinancialReports';
import ExportReports from './pages/finance/ExportReports';

// Import boost components
import BoostPackages from './pages/boost/BoostPackages';
import BoostPackageForm from './pages/boost/BoostPackageForm';
import BoostOrders from './pages/boost/BoostOrders';
import BoostAnalytics from './pages/boost/BoostAnalytics';
import BoostListings from './pages/boost/BoostListings';
import BoostOrderDetail from './pages/boost/BoostOrderDetail';

//Import categories components
import Categories from './pages/categories/Categories';
import CategoryForm from './pages/categories/CategoryForm';
import CategoryDetail from './pages/categories/CategoryDetail';
import CategoryAnalytics from './pages/categories/CategoryAnalytics';

//Import system components
import AdminUsers from './pages/system/AdminUsers';
import AdminForm from './pages/system/AdminForm';
import ActivityLogs from './pages/system/ActivityLogs';
import SystemSettings from './pages/system/SystemSettings';
import SystemHealth from './pages/system/SystemHealth';

import SupportTickets from './pages/support/SupportTickets';
import TicketDetail from './pages/support/TicketDetail';
import SupportStats from './pages/support/SupportStats';
import SupportCategories from './pages/support/SupportCategories';

import Disputes from './pages/disputes/Disputes';
import DisputeDetail from './pages/disputes/DisputeDetail';
import DisputeStats from './pages/disputes/DisputeStats';

import Reviews from './pages/reviews/Reviews';
import ReviewDetail from './pages/reviews/ReviewDetail';
import ReviewStats from './pages/reviews/ReviewStats';

import ReportedListings from './pages/reports/ReportedListings';
import ReportedSellers from './pages/reports/ReportedSellers';
import ReportDetail from './pages/reports/ReportDetail';
import SellerReportDetail from './pages/reports/SellerReportDetail';
import ReportStats from './pages/reports/ReportStats';

import Escrows from './pages/escrows/Escrows';
import EscrowDetail from './pages/escrows/EscrowDetail';
import EscrowDisputes from './pages/escrows/EscrowDisputes';
import EscrowStatistics from './pages/escrows/EscrowStatistics';


import { isAuthenticated, getCurrentAdmin, logout } from './services/api';

function App() {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    if (isAuthenticated()) {
      const adminData = getCurrentAdmin();
      setAdmin(adminData);
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, adminData) => {
    setAdmin(adminData);
  };

  const handleLogout = () => {
    logout();
    setAdmin(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/login" 
          element={
            admin ? <Navigate to="/admin/dashboard" replace /> : <Login onLogin={handleLogin} />
          } 
        />
        <Route 
          path="/admin/*" 
          element={
            admin ? (
              <Layout admin={admin} onLogout={handleLogout}>
                <Routes>
                  <Route path="dashboard" element={<Dashboard admin={admin} />} />
                  
                  {/* User Management Routes */}
                  <Route path="users" element={<Users admin={admin} />} />
                  <Route path="users/:userId" element={<UserDetail admin={admin} />} />
                  
                  {/* Seller Management Routes */}
                  <Route path="sellers" element={<Sellers admin={admin} />} />
                  <Route path="sellers/:sellerId" element={<SellerDetail admin={admin} />} />

                  {/* Listing Management Routes */}
                  <Route path="listings" element={<Listings admin={admin} />} />
                  <Route path="listings/:listingId" element={<ListingDetail admin={admin} />} />
                  <Route path="listings/moderation" element={<ListingModeration admin={admin} />} />

                  {/* Order Management Routes */}
                  <Route path="orders" element={<Orders admin={admin} />} />
                  <Route path="orders/:orderId" element={<OrderDetail admin={admin} />} />
                  <Route path="orders/disputes" element={<OrderDisputes admin={admin} />} />

                  {/* Financial Management Routes */}
                  <Route path="finance/transactions" element={<Transactions admin={admin} />} />
                  <Route path="finance/transactions/:transactionId" element={<TransactionDetail admin={admin} />} />
                  <Route path="finance/payments" element={<Payments admin={admin} />} />
                  <Route path="finance/payments/:paymentId" element={<PaymentDetail admin={admin} />} />
                  <Route path="finance/wallets" element={<Wallets admin={admin} />} />
                  <Route path="finance/wallets/:walletId" element={<WalletDetail admin={admin} />} /> 

                  {/* NEW FEE MANAGEMENT ROUTES */}
                  <Route path="finance/fee-settings" element={<FeeSettings admin={admin} />} /> 
                  <Route path="finance/fee-analytics" element={<FeeAnalytics admin={admin} />} /> 
                  <Route path="finance/fee-transactions" element={<FeeTransactions admin={admin} />} />

                  {/* UPDATED REPORTS ROUTES */}
                  <Route path="finance/reports" element={<FinancialReports admin={admin} />} />
                  <Route path="finance/reports/export" element={<ExportReports admin={admin} />} /> 

                  {/* Boost Management Routes */}
                  <Route path="boost/packages" element={<BoostPackages admin={admin} />} />
                  <Route path="boost/packages/new" element={<BoostPackageForm admin={admin} />} />
                  <Route path="boost/packages/edit/:packageId" element={<BoostPackageForm admin={admin} />} />
                  <Route path="boost/orders" element={<BoostOrders admin={admin} />} />
                  <Route path="boost/analytics" element={<BoostAnalytics admin={admin} />} />
                  <Route path="boost/listings" element={<BoostListings admin={admin} />} />
                  <Route path="boost/orders/:orderId" element={<BoostOrderDetail />} />

                  {/* Category Management Routes */}
                  <Route path="categories" element={<Categories admin={admin} />} />
                  <Route path="categories/new" element={<CategoryForm admin={admin} />} />
                  <Route path="categories/edit/:categoryId" element={<CategoryForm admin={admin} />} />
                  <Route path="categories/:categoryId" element={<CategoryDetail admin={admin} />} />
                  <Route path="categories/analytics" element={<CategoryAnalytics admin={admin} />} />

                    {/* System Management Routes */}
                  <Route path="system/admins" element={<AdminUsers admin={admin} />} />
                  <Route path="system/admins/new" element={<AdminForm admin={admin} />} />
                  <Route path="system/admins/edit/:adminId" element={<AdminForm admin={admin} />} />
                  <Route path="system/activities" element={<ActivityLogs admin={admin} />} />
                  <Route path="system/activities/:adminId" element={<ActivityLogs admin={admin} />} />
                  <Route path="system/settings" element={<SystemSettings admin={admin} />} />
                  <Route path="system/health" element={<SystemHealth admin={admin} />} />

                  <Route path="support/tickets" element={<SupportTickets admin={admin} />} />
                  <Route path="support/tickets/:ticketId" element={<TicketDetail admin={admin} />} />
                  <Route path="support/stats" element={<SupportStats admin={admin} />} />
                  <Route path="support/categories" element={<SupportCategories admin={admin} />} />

                  <Route path="disputes" element={<Disputes admin={admin} />} />
                  <Route path="disputes/:disputeId" element={<DisputeDetail admin={admin} />} />
                  <Route path="disputes/stats" element={<DisputeStats admin={admin} />} />

                  <Route path="reviews" element={<Reviews admin={admin} />} />
                  <Route path="reviews/stats" element={<ReviewStats admin={admin} />} />
                  <Route path="reviews/:reviewId" element={<ReviewDetail admin={admin} />} />

                  <Route path="reports/listings" element={<ReportedListings admin={admin} />} />
                  <Route path="reports/stats" element={<ReportStats admin={admin} />} />
                  <Route path="reports/sellers" element={<ReportedSellers admin={admin} />} />
                  <Route path="reports/listings/:reportId" element={<ReportDetail admin={admin} />} />
                  <Route path="reports/sellers/:reportId" element={<SellerReportDetail admin={admin} />} />

                  <Route path="escrows" element={<Escrows />} />
                  <Route path="escrows/:id" element={<EscrowDetail />} />
                  <Route path="escrows/disputes" element={<EscrowDisputes />} />
                  <Route path="escrows/statistics" element={<EscrowStatistics />} />

                  {/* Default redirect */}
                  <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Routes>
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route path="/" element={<Navigate to={admin ? "/admin/dashboard" : "/login"} replace />} />
      </Routes>
    </Router>
  );
}

export default App;